import React, { useState, useEffect, useCallback } from "react";
import { Swipeable } from "react-swipeable";
import { useHotelsStyles, useModuleBEM } from "@b2bportal/core-themes";
import { CoreHotelsComponent } from "@b2bportal/core-types";
import { MoveDirection, getNextValidIndex } from "@b2bportal/core-utilities";
import clsx from "clsx";
import { CarouselArrow } from "./Arrow";
import defaultStyles from "./ImageCarousel.module.scss";
import { CarouselImageSlider } from "./ImageSlider";
import { LodgingPlaceholder } from "./LodgingPlaceholder";
import { SliderDots } from "./SliderDots";

export interface ImageCarouselProps {
  className?: string;
  imageUrlsArray: string[];
  placeholder?: JSX.Element;
  initialIndex?: number;
  hideArrows?: boolean;
  hideDots?: boolean;
  smallVersion?: boolean;
  onClick?: () => void;
  onNavigateToPrevSlide?: () => void;
  onNavigateToNextSlide?: () => void;
  setIndex?: (index: number) => void;
  setHasValidImageIndex?: (hasValidIndex: boolean) => void;
  displayImageAsBackground?: boolean;
}

export const ImageCarousel: React.FC<ImageCarouselProps> = React.memo(
  ({
    className,
    imageUrlsArray,
    placeholder,
    initialIndex,
    hideArrows,
    hideDots,
    smallVersion,
    onClick,
    onNavigateToPrevSlide,
    onNavigateToNextSlide,
    setIndex,
    setHasValidImageIndex,
    displayImageAsBackground = true,
  }) => {
    const [currentIndex, setCurrentIndex] = useState<number>(initialIndex ?? 0);
    const [hasInitialValidIndex, setHasInitialValidIndex] =
      useState<boolean>(false);
    const [validIndexes, setValidIndexes] = useState<number[]>([]);
    const [hasFoundAllValidIndexes, setHasFoundAllValidIndexes] =
      useState<boolean>(false);
    const [goLeft, setGoLeft] = useState<boolean>(false);
    const [goRight, setGoRight] = useState<boolean>(false);

    // If the caller changes the initial index, this signals that we should also revert the carousel to show that index.
    useEffect(() => {
      setCurrentIndex(initialIndex ?? 0);
    }, [setCurrentIndex, initialIndex]);

    const COMPONENT_KEY = CoreHotelsComponent.ImageCarousel;
    const styles = useHotelsStyles(COMPONENT_KEY, defaultStyles);
    const [block, cn] = useModuleBEM(styles, COMPONENT_KEY);

    const updateValidIndexes = useCallback(
      (
        newValidIndex: number | null,
        moveDirection: MoveDirection,
        updateCurrentIndex: boolean
      ) => {
        if (newValidIndex === null) {
          setHasFoundAllValidIndexes(true);
          return null;
        }

        // this condition checks whether it has completed the carousel loop or not
        if (
          !hasFoundAllValidIndexes &&
          validIndexes.length > 1 &&
          ((validIndexes[0] === newValidIndex &&
            moveDirection === MoveDirection.Right) ||
            (validIndexes[validIndexes.length - 1] === newValidIndex &&
              moveDirection === MoveDirection.Left))
        ) {
          setHasFoundAllValidIndexes(true);
        } else if (!validIndexes.includes(newValidIndex)) {
          if (moveDirection === MoveDirection.Right) {
            validIndexes.push(newValidIndex);
          } else {
            validIndexes.unshift(newValidIndex);
          }
          setValidIndexes(validIndexes);
        }
        updateCurrentIndex && setCurrentIndex(newValidIndex);
        return null;
      },
      [hasFoundAllValidIndexes, validIndexes]
    );

    useEffect(() => {
      async function getInitialValidIndex() {
        if (!hasInitialValidIndex) {
          const newValidIndex = await getNextValidIndex(
            imageUrlsArray,
            currentIndex,
            MoveDirection.Right
          );
          updateValidIndexes(newValidIndex, MoveDirection.Right, true);
          setHasInitialValidIndex(true);
        }
      }

      getInitialValidIndex();
    }, [
      updateValidIndexes,
      imageUrlsArray,
      currentIndex,
      hasInitialValidIndex,
    ]);

    const handleNextValidIndex = useCallback(
      async (
        imageUrlsArray: string[],
        indexUnvalidated: number,
        moveDirection: MoveDirection,
        updateCurrentIndex: boolean
      ) => {
        const newValidIndex = await getNextValidIndex(
          imageUrlsArray,
          indexUnvalidated,
          moveDirection
        );
        updateValidIndexes(newValidIndex, moveDirection, updateCurrentIndex);
      },
      [updateValidIndexes, setIndex]
    );

    const validatePrevSlide = useCallback(
      (updateCurrentIndex: boolean) => {
        if (hasFoundAllValidIndexes && validIndexes.length !== 0) {
          const index = validIndexes.findIndex((ind) => ind === currentIndex);
          const prevIndex =
            (index - 1 + validIndexes.length) % validIndexes.length;
          updateCurrentIndex && setCurrentIndex(validIndexes[prevIndex]);
        } else if (imageUrlsArray.length !== 0) {
          const prevIndexUnvalidated =
            (currentIndex - 1 + imageUrlsArray.length) % imageUrlsArray.length;
          handleNextValidIndex(
            imageUrlsArray,
            prevIndexUnvalidated,
            MoveDirection.Left,
            updateCurrentIndex
          );
        }
      },
      [
        handleNextValidIndex,
        hasFoundAllValidIndexes,
        imageUrlsArray,
        currentIndex,
        validIndexes,
      ]
    );

    const validateNextSlide = useCallback(
      (updateCurrentIndex: boolean) => {
        if (hasFoundAllValidIndexes && validIndexes.length !== 0) {
          const index = validIndexes.findIndex((ind) => ind === currentIndex);
          const nextIndex = (index + 1) % validIndexes.length;
          updateCurrentIndex && setCurrentIndex(validIndexes[nextIndex]);
        } else if (imageUrlsArray.length !== 0) {
          const nextIndexUnvalidated =
            (currentIndex + 1) % imageUrlsArray.length;
          handleNextValidIndex(
            imageUrlsArray,
            nextIndexUnvalidated,
            MoveDirection.Right,
            updateCurrentIndex
          );
        }
      },
      [
        handleNextValidIndex,
        hasFoundAllValidIndexes,
        imageUrlsArray,
        currentIndex,
        validIndexes,
      ]
    );

    useEffect(() => {
      if (setIndex) setIndex(currentIndex);
    }, [currentIndex]);

    useEffect(() => {
      if (setHasValidImageIndex) setHasValidImageIndex(validIndexes.length > 0);
    }, [validIndexes.length]);

    const goToPrevSlide = useCallback(() => {
      !hideDots && setGoLeft(true);
      validatePrevSlide(true);
      onNavigateToPrevSlide && onNavigateToPrevSlide();
    }, [onNavigateToPrevSlide, validatePrevSlide, hideDots]);

    const goToNextSlide = useCallback(() => {
      !hideDots && setGoRight(true);
      validateNextSlide(true);
      onNavigateToNextSlide && onNavigateToNextSlide();
    }, [onNavigateToNextSlide, validateNextSlide, hideDots]);

    const imageToShow = validIndexes.length ? (
      <CarouselImageSlider
        url={imageUrlsArray[currentIndex]}
        displayImageAsBackground={displayImageAsBackground}
      ></CarouselImageSlider>
    ) : (
      (placeholder ?? (
        <LodgingPlaceholder
          displayImageAsBackground={displayImageAsBackground}
        />
      ))
    );

    return (
      <Swipeable
        className={clsx(block, cn("wrapper"), className)}
        preventDefaultTouchmoveEvent
        onSwipedLeft={goToNextSlide}
        onSwipedRight={goToPrevSlide}
      >
        <div className={cn("container")}>
          {onClick && (
            <button
              className={cn("slider-button-wrapper")}
              component="button"
              onClick={onClick}
            >
              {imageToShow}
            </button>
          )}
          {!onClick && imageToShow}
          {!hideDots &&
            validIndexes.length > 0 &&
            imageUrlsArray.length > 1 && (
              <SliderDots
                goLeft={goLeft}
                setGoLeft={setGoLeft}
                goRight={goRight}
                setGoRight={setGoRight}
                smallVersion={smallVersion}
                numberOfImages={imageUrlsArray.length}
                currentIndex={currentIndex}
              />
            )}
          {!hideArrows &&
            validIndexes.length > 0 &&
            imageUrlsArray.length > 1 && (
              <>
                <CarouselArrow
                  direction="left"
                  onClickEvent={goToPrevSlide}
                ></CarouselArrow>
                <CarouselArrow
                  direction="right"
                  onClickEvent={goToNextSlide}
                ></CarouselArrow>
              </>
            )}
        </div>
      </Swipeable>
    );
  }
);
